import {
  Alert,
  Button,
  Card,
  Tabs,
  Modal,
  ModalBody,
  ModalHeader,
} from "flowbite-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  formatToReal,
  isValidCNPJ,
  isValidCPF,
  isSourceRestricted,
} from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import HeaderQuery from "./HeaderQuery";
import CardQueryDescription from "../common/CardQueryDescription";
import QueryInput from "../inputs/QueryInput";
import { useQuery } from "react-query";
import DrawerLoading from "../DrawerLoading";
import AcertaEssencialQuery from "./custom-queries-credit/AcertaEssencialQuery";
import LocalizacaoPFQuery from "./custom-queries-credit/LocalizacaoPFQuery";
import DefineRiscoQuery from "./custom-queries-credit/DefineRiscoQuery";
import LocalizacaoPJQuery from "./custom-queries-credit/LocalizacaoPJQuery";
import Protestos from "../common/query/Protestos";
import TitleStripe from "../common/TitleStripe";
import NoOcurrence from "../common/query/NoOcurrence";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import ScrBacenQuery from "./custom-queries-credit/ScrBacenQuery";
import { useReactToPrint } from "react-to-print";
import { FaPrint } from "react-icons/fa6";
import AcertaEssencialPositivoQuery from "./custom-queries-credit/AcertaEssencialPositivoQuery";
import PendenciasSerasaQuery from "./custom-queries-credit/PendenciasSerasaQuery";
import PreparePrint from "../common/PreparePrint";
import ScorePFQuery from "./custom-queries-credit/ScorePFQuery";
import ScorePJQuery from "./custom-queries-credit/ScorePJQuery";
import FooterLGPD from "../common/query/FooterLGPD";
import ErrorQuery from "../common/query/ErrorQuery";
import SPCSerasaPJ from "./custom-queries-credit/SPCSerasaPJ";
import SPCSerasaPF from "./custom-queries-credit/SPCSerasaPF";
import CadinQuery from "./custom-queries-credit/CadinQuery";
import LoadingAnaliseIA from "../common/query/LoadingAnaliseIA";
import AnaliseDeCreditoPlaceholder from "../common/query/AnaliseDeCreditoPlaceholder";
import DefineLimitePositivoQuery from "./DefineLimitePositivoQuery";
import DefineLimiteQuery from "./DefineLimiteQuery";
import PendenciasQuod from "./custom-queries-credit/PendenciasQuod";
import JudicialProcesses from "./custom-queries-credit/JudicialProcesses";
import DividasQuery from "./custom-queries-credit/DividasQuery";
import CrednetQuery from "./custom-queries-credit/CrednetQuery";
import SerasaPremiumQuery from "./custom-queries-credit/SerasaPremiumQuery";
import { FaRegFilePdf } from "react-icons/fa";
import PendenciasSPCSerasaPF from "./custom-queries-credit/PendenciasSPCSerasaPF";
import CadastralPFQuery from "./custom-queries-cadastral/CadastralPFQuery";
import CadastralPJQuery from "./custom-queries-cadastral/CadastralPJQuery";
import SCPCQuery from "./custom-queries-credit/SCPCQuery";
import CreditoPreditivo from "./custom-queries-credit/CreditoPreditivo";
import ScoreRating from "./custom-queries-credit/ScoreRating";
import CustomCreditQueryAdditional from "./CustomCreditQueryAdditional";
import RendaPresumidaPF from "./custom-queries-credit/RendaPresumidaPF";
import GastoEstimadoPF from "./custom-queries-credit/GastoEstimadoPF";
import CadastralPremiumPFQuery from "./custom-queries-cadastral/CadastralPremiumPFQuery";
import CadastralPremiumPJQuery from "./custom-queries-cadastral/CadastralPremiumPJQuery";
import DefineNegocio from "./custom-queries-credit/DefineNegocio";
import AcertaCompletoPositivoQuery from "./custom-queries-credit/AcertaCompletoPositivoQuery";
import ScpcNetPFQuery from "./custom-queries-credit/ScpcNetPFQuery";
import SpcMixTopQuery from "./custom-queries-credit/SpcMixTopQuery";
import InformacoesFinanceirasPJQuery from "./custom-queries-credit/InformacoesFinanceirasPJQuery";
import CustomAlertWrapper from "./CustomAlertWrapper";
import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi";
import SerasaBasicoQuery from "./custom-queries-credit/SerasaBasicoQuery";
import { TbHandClick } from "react-icons/tb";
import DadosReceitaFederalPJ from "./custom-queries-cadastral/DadosReceitaFederalPJ";
import ConsultaLoading from "../ConsultaLoading";
import { MotorCredito } from "./custom-queries-credit/MotorCredito";
import SpcTerceirosQuery from "./custom-queries-credit/SpcTerceirosQuery";
import SpcMaisQuery from "./custom-queries-credit/SpcMaisQuery";
import DefineLimitePositivoQueryV2 from "./DefineLimitePositivoQueryV2";
import FaturamentoPresumido from "./custom-queries-credit/FaturamentoPresumido";
import LimiteCreditoPJ from "./custom-queries-credit/LimiteCreditoPJ";
import CadastralRestritivoScoreQueryPJ from "./custom-queries-credit/CadastralRestritivoScoreQueryPJ";
import CadastralRestritivoScoreQueryPF from "./custom-queries-credit/CadastralRestritivoScoreQueryPF";
import { FiAlertCircle } from "react-icons/fi";

/* ===== Chaves financeiras (render em bloco único) ===== */
const FINANCE_KEYS = new Set([
  "recebimentosViaBoletoPJ",
  "pagamentosViaBoletoPJ",
  "predicaoRecebimentoBoletoPJ",
  "concentracaoRecebimentoPJ",
  "concentracaoPagamentoPJ",
  "liquidezPagamentoPJ",
  "liquidezRecebimentoPJ",
  "mediaDiasInadimpleciaPJ",
  "recebimentosViaBoletoCartaoTedPJ",
  "pagamentosViaBoletoTedPJ",
  "scoreEvolucaoPJ",
]);

/* ===== Agrupamento por "fonte" (tabs) ===== */
const SOURCE_SETS = {
  boavista: new Set([
    "defineRisco",
    "defineNegocioPositivo",
    "defineLimite",
    "defineLimitePositivo",
    "acertaEssencialPositivo",
    "acertaEssencial",
    "acertaCompleto",
  ]),
  serasa: new Set(["crednet", "serasa", "serasaPremium"]),
  spc: new Set([
    "spcMixTopPF",
    "spcMixTopPJ",
    "spcSerasaPF",
    "spcSerasaPJ",
    "pendSPCSerasaPF",
    "spcMais",
  ]),
  quod: new Set(["quod"]),
  scrBacen: new Set(["scrBacen"]),
  receitaFederalPJ: new Set(["receitaFederalPJ"]),
};

/* ===== Helpers para additional ===== */
function coerceAdditionalList(additionalRaw) {
  if (!additionalRaw) return [];
  let add = additionalRaw;
  if (typeof add === "string") {
    try {
      const t = add.trim();
      add = t && t !== "null" && t !== "undefined" ? JSON.parse(t) : null;
    } catch {
      add = null;
    }
  }
  if (!add) return [];
  if (Array.isArray(add)) return add.filter(Boolean);
  if (typeof add === "object") return Object.values(add).filter(Boolean);
  return [];
}

function hasRestrictionsAdditional(addList) {
  try {
    for (const it of addList) {
      const payload =
        it?.data != null
          ? it.data
          : it?.consultaCredito != null
          ? it.consultaCredito
          : it;

      const rc = payload?.resumoConsulta || {};
      const anyQty =
        Number(rc?.pendenciasFinanceiras?.quantidadeTotal || 0) > 0 ||
        Number(rc?.protestos?.quantidadeTotal || 0) > 0 ||
        Number(rc?.chequesSemFundo?.quantidadeTotal || 0) > 0 ||
        Number(rc?.spc?.quantidadeTotal || 0) > 0 ||
        Number(rc?.serasa?.quantidadeTotal || 0) > 0;

      const anyVal =
        Number(
          String(rc?.pendenciasFinanceiras?.valorTotal || "0").replace(
            /[^\d]/g,
            ""
          )
        ) > 0 ||
        Number(String(rc?.protestos?.valorTotal || "0").replace(/[^\d]/g, "")) >
          0;

      const ocorrenciasLen =
        (payload?.ocorrencias && payload.ocorrencias.length) || 0;

      if (anyQty || anyVal || ocorrenciasLen > 0) return true;
    }
  } catch {}
  return false;
}

function guessAdditionalKey(item = {}) {
  const tryKeys = [
    item.key,
    item.alias,
    item.nome,
    item.name,
    item.tipo,
    item.type,
  ];
  const first = tryKeys.find(Boolean);
  if (first) return String(first);
  if (item?.consultaCredito?.scoreRating) return "scoreRating";
  return null;
}

function hasRestrictionsInResumo(resumo = {}) {
  const qty =
    Number(resumo?.pendenciasFinanceiras?.quantidadeTotal || 0) +
    Number(resumo?.protestos?.quantidadeTotal || 0) +
    Number(resumo?.chequesSemFundo?.quantidadeTotal || 0) +
    Number(resumo?.spc?.quantidadeTotal || 0) +
    Number(resumo?.serasa?.quantidadeTotal || 0) +
    Number(resumo?.acoesCiveis?.quantidadeTotal || 0);
  const val =
    Number(
      String(resumo?.pendenciasFinanceiras?.valorTotal || "0").replace(
        /[^\d]/g,
        ""
      )
    ) +
    Number(String(resumo?.protestos?.valorTotal || "0").replace(/[^\d]/g, ""));
  return qty > 0 || val > 0;
}

function coerceResumoConsulta(node) {
  if (!node) return null;

  // Se já vier pronto, só reaproveita
  if (node.resumoConsulta) return node.resumoConsulta;
  if (node?.consultaCredito?.resumoConsulta)
    return node.consultaCredito.resumoConsulta;

  // Tentativa de mapear retornos crus da Boa Vista (Define/Acerta/Positivo)
  const hasBoavistaRaw =
    node?.protestos ||
    node?.pendencias_restricoes ||
    node?.acoes_civeis ||
    node?.falencias_recuperacao_judicial ||
    node?.cheques_sem_fundo;

  if (!hasBoavistaRaw) return null;

  const protestos = {
    quantidadeTotal: Number(node?.protestos?.total_protestos || 0),
    valorTotal: Number(node?.protestos?.valor_total || 0),
  };

  const pendenciasFinanceiras = {
    quantidadeTotal: Number(node?.pendencias_restricoes?.total_pendencias || 0),
    valorTotal: Number(node?.pendencias_restricoes?.valor || 0),
  };

  const acoesCiveis = {
    quantidadeTotal: Number(node?.acoes_civeis?.total_acoes || 0),
    valorTotal: Number(node?.acoes_civeis?.valor_total || 0),
  };

  const falenciasRecJudiciais = {
    quantidadeTotal: Number(
      node?.falencias_recuperacao_judicial?.quantidade_total || 0
    ),
    ultimaOcorrencia:
      node?.falencias_recuperacao_judicial?.periodo_final || null,
  };

  const chequesSemFundo = {
    quantidadeTotal: Number(
      node?.cheques_sem_fundo?.total_cheques_sem_fundos || 0
    ),
  };

  return {
    protestos,
    pendenciasFinanceiras,
    acoesCiveis,
    falenciasRecJudiciais,
    chequesSemFundo,
  };
}

function computeSourceRestricted(data, sourceKey) {
  const set = SOURCE_SETS[sourceKey];
  if (!set || !data) return false;

  for (const key of set) {
    const node = data?.[key];
    if (!node) continue;

    // Sempre tenta gerar/obter um resumoConsulta padronizado
    const resumo = coerceResumoConsulta(node);
    if (resumo && hasRestrictionsInResumo(resumo)) return true;

    // fallback: alguns retornos usam "ocorrencias" diretamente
    if (Array.isArray(node?.ocorrencias) && node.ocorrencias.length > 0)
      return true;
  }
  return false;
}

function mapKeyToSource(key) {
  if (!key) return null;
  const k = String(key);
  for (const [source, set] of Object.entries(SOURCE_SETS)) {
    if (set.has(k)) return source;
  }
  if (/quod/i.test(k)) return "quod";
  if (/scrBacen/i.test(k)) return "scrBacen";
  if (/serasa|crednet/i.test(k)) return "serasa";
  if (/spc|protesto|cheque|scpc/i.test(k)) return "spc";
  if (/define|acerta/i.test(k)) return "boavista";
  if (/receitaFederalPJ/i.test(k)) return "receitaFederalPJ";
  return null;
}

/* ==========================
   Tabs (mantidas)
   ========================== */
function TabbedBodyReview({ data, document, renderComponentByKey }) {
  const additionalList = coerceAdditionalList(data?.additional);

  const hasBV =
    !!data && Array.from(SOURCE_SETS.boavista).some((k) => data?.[k] != null);
  const hasSerasa = !!data?.crednet || !!data?.serasa || !!data?.serasaPremium;
  const hasSPC =
    !!data?.spcMixTopPF ||
    !!data?.spcMixTopPJ ||
    !!data?.spcSerasaPF ||
    !!data?.spcSerasaPJ ||
    !!data?.spcMais ||
    !!data?.pendSPCSerasaPF;
  const hasQuod = !!data?.quod;
  const hasReceitaFederal = data?.receitaFederalPJ;

  const hasJudicial = data?.acoesProcessos?.statusRetorno == 1;
  const judicialRestricted =
    (data?.acoesProcessos?.acoes?.processos?.length || 0) > 0;

  const hasCadin = data?.cadin?.statusRetorno == 1;
  const hasProtestoNacional = data?.protestoNacional;
  const protestoRestricted =
    (data?.protestoNacional?.ocorrencias?.length || 0) > 0;

  const hasScr = data?.scrBacen?.statusRetorno == 1;

  function computeAllRestricted() {
    const src =
      computeSourceRestricted(data, "boavista") ||
      computeSourceRestricted(data, "serasa") ||
      computeSourceRestricted(data, "spc") ||
      computeSourceRestricted(data, "quod");
    const extras =
      judicialRestricted ||
      protestoRestricted ||
      hasRestrictionsAdditional(additionalList);
    return !!(src || extras);
  }

  const tabs = [];
  tabs.push({
    key: "all",
    title: "Ver tudo",
    restricted: computeAllRestricted(),
  });

  if (hasBV)
    tabs.push({
      key: "boavista",
      title: "Fonte 1",
      restricted: computeSourceRestricted(data, "boavista"),
    });
  if (hasSerasa)
    tabs.push({
      key: "serasa",
      title: "Fonte 2",
      restricted: computeSourceRestricted(data, "serasa"),
    });
  if (hasSPC)
    tabs.push({
      key: "spc",
      title: "Fonte 3",
      restricted: computeSourceRestricted(data, "spc"),
    });
  if (hasQuod)
    tabs.push({
      key: "quod",
      title: "Fonte 4",
      restricted: computeSourceRestricted(data, "quod"),
    });

  if (hasJudicial) {
    tabs.push({
      key: "acoesProcessos",
      title: "Ações/Processos",
      restricted: judicialRestricted,
    });
  }
  if (hasCadin) {
    tabs.push({
      key: "cadin",
      title: "CADIN",
      restricted: +data?.cadin?.qtd_dividas > 0,
    });
  }
  if (hasProtestoNacional) {
    tabs.push({
      key: "protestoNacional",
      title: "Protesto Nacional",
      restricted: protestoRestricted,
    });
  }
  if (hasScr) {
    tabs.push({
      key: "scrBacen",
      title: "SCR/Bacen",
      restricted: false,
    });
  }
  if (hasReceitaFederal) {
    tabs.push({
      key: "receitaFederalPJ",
      title: "Receita Federal",
      restricted: false,
    });
  }
  if (additionalList.length > 0) {
    tabs.push({
      key: "additional",
      title: "Adicionais",
      restricted: hasRestrictionsAdditional(additionalList),
      count: additionalList.length,
    });
  }

  const [activeKey, setActiveKey] = useState(tabs[0]?.key || "");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!tabs.find((t) => t.key === activeKey)) {
      setActiveKey(tabs[0]?.key || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tabs)]);

  const renderDataBySource = (source) => {
    const set = SOURCE_SETS[source];
    if (!set) return null;

    const base = Object.entries(data || {})
      .filter(([k, v]) => v && v.prioridade != null && set.has(String(k)))
      .map(([k, v]) => ({
        key: String(k),
        prioridade: Number(v.prioridade),
        dataItem: v,
      }));

    const extras = coerceAdditionalList(data?.additional)
      .map((it, idx) => {
        const guessedKey = guessAdditionalKey(it);
        const src = mapKeyToSource(guessedKey);
        if (src !== source) return null;

        const payload =
          it?.data != null
            ? it.data
            : it?.consultaCredito != null
            ? it.consultaCredito
            : it;

        const prio = Number(it?.prioridade ?? 9999 + idx);
        return {
          key: String(guessedKey || `additional_${idx + 1}`),
          prioridade: prio,
          dataItem: payload,
        };
      })
      .filter(Boolean);

    const merged = [...base, ...extras].sort(
      (a, b) => a.prioridade - b.prioridade
    );
    if (!merged.length) return null;

    return (
      <>
        {merged.map((item, i) => (
          <React.Fragment key={`${source}_${item.key}_${i}`}>
            {renderComponentByKey(item.key, item.dataItem, document)}
          </React.Fragment>
        ))}
      </>
    );
  };

  const renderAll = () => {
    if (!data) return null;

    const itemsArray = Object.entries(data)
      .filter(([_, value]) => value && value.prioridade != null)
      .map(([key, value]) => ({
        key,
        prioridade: Number(value.prioridade),
        dataItem: value,
      }));

    const extras = coerceAdditionalList(data?.additional).map((it, idx) => {
      const guessedKey = guessAdditionalKey(it) || `additional_${idx + 1}`;
      const payload =
        it?.data != null
          ? it.data
          : it?.consultaCredito != null
          ? it.consultaCredito
          : it;
      const prio = Number(it?.prioridade ?? 9999 + idx);
      return { key: String(guessedKey), prioridade: prio, dataItem: payload };
    });

    const merged = [...itemsArray, ...extras].sort(
      (a, b) => a.prioridade - b.prioridade
    );

    const anyActiveFinance = Array.from(FINANCE_KEYS).some(
      (k) => data?.[k]?.statusRetorno == 1
    );
    let financeRendered = false;

    return (
      <>
        {merged.map(({ key, dataItem }, i) => {
          if (FINANCE_KEYS.has(key)) {
            if (financeRendered) return null;
            financeRendered = true;
            return anyActiveFinance ? (
              <InformacoesFinanceirasPJQuery
                key={`__finance___${i}`}
                dataFull={data}
              />
            ) : null;
          }
          return (
            <React.Fragment key={`all_${key}_${i}`}>
              {renderComponentByKey(key, dataItem, document)}
            </React.Fragment>
          );
        })}
        {coerceAdditionalList(data?.additional).length > 0 && (
          <div className="mt-2">
            <CustomCreditQueryAdditional data={data?.additional} />
          </div>
        )}
      </>
    );
  };

  if (!tabs.length) return null;

  const RestrictionIcon = ({ restricted }) =>
    restricted ? (
      <HiExclamationCircle
        className="ml-2 h-4 w-4 text-red-600"
        title="Com restrições"
        aria-label="Com restrições"
      />
    ) : (
      <HiCheckCircle
        className="ml-2 h-4 w-4 text-blue-600"
        title="Sem restrições"
        aria-label="Sem restrições"
      />
    );

  const CountBadge = ({ children }) => (
    <span className="ml-2 inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600 w-6 justify-center">
      {children}
    </span>
  );

  return (
    <div className="mt-4">
      <div className="">
        <div className="">
          <div className="px-2 mb-1 flex items-center justify-between">
            <div className="font-semibold">
              <TbHandClick className="inline-block mr-1" /> Use as abas para
              alternar entre fontes.
            </div>
            <div className="text-gray-600 text-xs italic">
              Dica: “Ver tudo” mostra todos os blocos em ordem de prioridade.
            </div>
          </div>
          <div className="relative flex items-center">
            <div
              ref={scrollRef}
              role="tablist"
              aria-label="Resultados por fonte"
              className="hide-scrollbar -my-px flex w-full gap-2 overflow-x-auto px-2 py-2"
            >
              {tabs.map((t) => {
                const active = t.key === activeKey;
                return (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveKey(t.key)}
                    className={[
                      "group relative inline-flex items-center whitespace-nowrap rounded-xl px-3.5 py-2 text-sm transition",
                      active
                        ? "bg-slate-600 text-white shadow-sm"
                        : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <span className="font-semibold">{t.title}</span>
                    <RestrictionIcon restricted={t.restricted} />
                    {t.key === "additional" && t.count != null && (
                      <CountBadge>{t.count}</CountBadge>
                    )}
                    <span
                      className={[
                        "pointer-events-none absolute inset-x-2 -bottom-[2px] h-0.5 rounded-full",
                        active ? "bg-white/70" : "bg-transparent",
                      ].join(" ")}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100 animate-[fadeIn_200ms_ease-out]">
        <div className="-mt-6">
          {activeKey === "all" && renderAll()}
          {activeKey === "boavista" && renderDataBySource("boavista")}
          {activeKey === "serasa" && renderDataBySource("serasa")}
          {activeKey === "spc" && renderDataBySource("spc")}
          {activeKey === "quod" && renderDataBySource("quod")}
          {activeKey === "acoesProcessos" &&
            renderComponentByKey(
              "acoesProcessos",
              data?.acoesProcessos,
              document
            )}
          {activeKey === "cadin" &&
            renderComponentByKey("cadin", data?.cadin, document)}
          {activeKey === "protestoNacional" &&
            renderComponentByKey(
              "protestoNacional",
              data?.protestoNacional,
              document
            )}
          {activeKey === "scrBacen" &&
            renderComponentByKey("scrBacen", data?.scrBacen, document)}
          {activeKey === "receitaFederalPJ" &&
            renderComponentByKey(
              "receitaFederalPJ",
              data?.receitaFederalPJ,
              document
            )}
          {activeKey === "additional" && (
            <div className="mt-2">
              <CustomCreditQueryAdditional data={data?.additional} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==========================
   Componente Principal
   ========================== */
export default function CustomCreditQuery({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
  userQueryPermissions = [],
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [selectedAdditionalIds, setSelectedAdditionalIds] = useState([]);
  const [isDoneRequest, setIsDoneRequest] = useState(false);
  const [error, setError] = useState("");
  const [errorQuery, setErrorQuery] = useState(0);
  const [data, setData] = useState(null);
  const [document, setDocument] = useState("");
  const [isLoadingIA, setIsLoadingIA] = useState(false);
  const [resultadoIA, setResultadoIA] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  /* ===== NEW: detecção de mobile + modal ===== */
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  /* abre modal automaticamente ao concluir consulta no mobile */
  useEffect(() => {
    if (isMobile && isDoneRequest && (data || errorQuery)) {
      setShowMobileModal(true);
    }
  }, [isMobile, isDoneRequest, data, errorQuery]);

  const componentRef = useRef(null);
  const printRef = useRef();
  const scrolledRef = useRef(false);

  const handleAfterPrint = useCallback(() => setIsPrinting(false), []);
  const handleBeforePrint = useCallback(() => {
    setIsPrinting(true);
    return Promise.resolve();
  }, []);

  const printFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `consulta_${document}`,
    onAfterPrint: handleAfterPrint,
    onBeforePrint: handleBeforePrint,
  });

  const { isLoading: isLoadingCustomQuery, data: customQuery } = useQuery(
    ["customQuery", query],
    async () => {
      const { data: items } = await axios.get(
        `${QUERIES.GET_QUERY}/${query?.id}`
      );
      return items.data;
    }
  );

  useEffect(() => {
    setData(null);
    setInfo("");
    setError("");
    setErrorQuery(0);
    setDocument("");
    setResultadoIA(null);
    setShowMobileModal(false); // fecha modal se trocar de consulta
  }, [query]);

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage);
      setDocument(dataFromLocalStorage?.resumoRetorno?.document);
      setIsDoneRequest(true);
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInfo = async (generatePDF = false) =>
    axios(
      `${QUERIES.GET_V2_QUERY}/${customQuery?.url}/${info.replace(/\D/g, "")}` +
        `?generatePDF=${generatePDF}&additionalQueryIds=${
          selectedAdditionalIds.length ? selectedAdditionalIds.join(",") : ""
        }`
    );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDocument(info);
    scrolledRef.current = false;

    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;

    if (customQuery?.QueryCustomized?.parameterType === "CPF") {
      if (!isValidCPF(unmaskedValue)) {
        setError("CPF inválido!");
        isValid = false;
      }
    } else if (customQuery?.QueryCustomized?.parameterType === "CNPJ") {
      if (!isValidCNPJ(unmaskedValue)) {
        setError("CNPJ inválido!");
        isValid = false;
      }
    }

    if (!isValid) {
      setData(null);
      return;
    }

    try {
      setData(null);
      setIsLoading(true);
      setIsDoneRequest(false);
      setError("");
      setErrorQuery(0);
      setResultadoIA(null);

      const { data: dados } = await fetchInfo();
      setData(dados.data);
      if (dados?.data?.IA) setResultadoIA(dados.data.IA);
    } catch (err) {
      const status = err?.response?.status || 500;
      setErrorQuery(status);
    } finally {
      setIsLoading(false);
      setIsDoneRequest(true);
    }
  };

  const savePDFFn = async () => {
    setIsPrinting(true);
    try {
      const response = await fetchInfo(true);
      const pdfBase64 = response?.data?.data?.resumoRetorno?.pdfBase64;
      const documentId = response?.data?.data?.resumoRetorno?.document;
      if (!pdfBase64) return;
      const byteArray = Uint8Array.from(atob(pdfBase64), (c) =>
        c.charCodeAt(0)
      );
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const fileName = `consulta_${documentId || "documento"}.pdf`;

      if (isMobile && navigator?.canShare) {
        try {
          const file = new File([blob], fileName, { type: "application/pdf" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: fileName,
            });
            return;
          }
        } catch {}
      }

      const url = URL.createObjectURL(blob);
      if (isMobile) {
        const newTab = window.open(url, "_blank", "noopener,noreferrer");
        if (!newTab) {
          window.location.href = url;
        }
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        return;
      }
      const link = window.document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.rel = "noopener";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPrinting(false);
    }
  };

  const rawAdditionalOptions =
    customQuery?.additionalQueries || query?.additionalQueries || [];

  const permittedAdditionalOptions = useMemo(() => {
    if (
      !Array.isArray(rawAdditionalOptions) ||
      !Array.isArray(userQueryPermissions)
    )
      return [];
    const permByQueryId = new Map(
      userQueryPermissions.map((p) => [Number(p.QueryId ?? p.id), p])
    );
    return rawAdditionalOptions
      .map((opt) => {
        const perm = permByQueryId.get(Number(opt.id));
        if (!perm) return null;
        if (Number(perm.status) !== 1 || !perm.isAdditional) return null;
        const priceFromPerm = perm.price != null ? String(perm.price) : null;
        return { ...opt, price: priceFromPerm ?? opt.price };
      })
      .filter(Boolean);
  }, [rawAdditionalOptions, userQueryPermissions]);

  useEffect(() => {
    const el = componentRef.current;
    const shouldScroll = isDoneRequest && (data || errorQuery);
    if (shouldScroll && el && !scrolledRef.current) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      scrolledRef.current = true;
    }
  }, [isDoneRequest, data, errorQuery]);

  const dataAlerts = useMemo(() => {
    if (!data) return null;
    const merged = { ...data };
    let add = data?.additional;
    if (typeof add === "string") {
      try {
        const t = add.trim();
        add = t && t !== "null" && t !== "undefined" ? JSON.parse(t) : null;
      } catch {
        add = null;
      }
    }
    if (!add) return merged;
    if (Array.isArray(add)) {
      add.forEach((item, idx) => {
        if (!item) return;
        const key =
          item?.key ||
          item?.alias ||
          item?.nome ||
          item?.name ||
          `additional_${idx + 1}`;
        merged[key] = item;
      });
    } else if (typeof add === "object") {
      Object.entries(add).forEach(([k, v]) => {
        if (v != null) merged[k] = v;
      });
    }
    return merged;
  }, [data]);

  const renderComponentByKey = (key, dataItem, doc) => {
    switch (key) {
      case "receitaFederalPJ":
        return <DadosReceitaFederalPJ data={dataItem} dataFull={data} />;
      case "cadastralCompleto":
        return <CadastralPFQuery data={dataItem} dataFull={data} />;
      case "cadastralCompletoPJ":
        return <CadastralPJQuery data={dataItem} dataFull={data} />;
      case "cadastralPremiumPF":
        return <CadastralPremiumPFQuery data={dataItem} dataFull={data} />;
      case "cadastralPremiumPJ":
        return <CadastralPremiumPJQuery data={dataItem} dataFull={data} />;
      case "localizacao":
        return doc?.length === 14 ? (
          <LocalizacaoPFQuery data={dataItem} />
        ) : (
          <LocalizacaoPJQuery data={dataItem} />
        );
      case "acertaEssencial":
        return (
          <AcertaEssencialQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
          />
        );
      case "scpcNetPF":
        return (
          <ScpcNetPFQuery data={dataItem?.consultaCredito} dataFull={data} />
        );
      case "crednet":
        return <CrednetQuery data={dataItem.consultaCredito} dataFull={data} />;
      case "serasaRelatorioBasicoPF":
      case "serasaRelatorioBasicoPJ":
        return (
          <SerasaBasicoQuery data={dataItem.consultaCredito} dataFull={data} />
        );
      case "serasaPremium":
        return <SerasaPremiumQuery data={dataItem.consultaCredito} />;
      case "acertaEssencialPositivo":
        return (
          <AcertaEssencialPositivoQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
          />
        );
      case "acertaCompleto":
        return (
          <AcertaCompletoPositivoQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
          />
        );
      case "defineRisco":
        return (
          <DefineRiscoQuery data={dataItem?.consultaCredito} dataFull={data} />
        );
      case "defineNegocioPositivo":
        return (
          <DefineNegocio data={dataItem?.consultaCredito} dataFull={data} />
        );
      case "defineLimite":
        return <DefineLimiteQuery data={dataItem} dataFull={data} />;
      case "defineLimitePositivo":
        return <DefineLimitePositivoQuery data={dataItem} dataFull={data} />;
      case "spcSerasaPJ":
        return <SPCSerasaPJ data={dataItem} />;
      case "spcSerasaPF":
        return <SPCSerasaPF data={dataItem} />;
      case "pendSPCSerasaPF":
        return <PendenciasSPCSerasaPF data={dataItem} dataFull={data} />;
      case "serasa":
        return <PendenciasSerasaQuery data={dataItem} />;
      case "spcMixTopPF":
      case "spcMixTopPJ":
        return <SpcMixTopQuery data={dataItem} />;
      case "spcTerceirosPF":
      case "spcTerceirosPJ":
        return <SpcTerceirosQuery data={dataItem} />;
      case "spcMais":
        return <SpcMaisQuery data={dataItem} />;
      case "quod":
        return <PendenciasQuod data={dataItem} />;
      case "cadastralRestritivoScorePJ":
        return (
          <CadastralRestritivoScoreQueryPJ
            data={dataItem.consultaCredito}
            dataFull={data}
          />
        );
      case "cadastralRestritivoScorePF":
        return (
          <CadastralRestritivoScoreQueryPF
            data={dataItem.consultaCredito}
            dataFull={data}
          />
        );
      case "faturamentoPresumidoPJ":
        return <FaturamentoPresumido data={dataItem} />;
      case "limiteCreditoPJ":
        return <LimiteCreditoPJ data={dataItem} />;
      case "protestoNacional":
        return (
          <div id="protesto-nacional">
            {dataItem?.statusRetorno == 1 ? (
              <>
                <TitleStripe className="mb-4">Protesto Nacional</TitleStripe>
                {dataItem?.ocorrencias?.length > 0 ? (
                  <Protestos
                    quantidadeTotal={
                      dataItem?.resumoConsulta?.protestos?.quantidadeTotal
                    }
                    valorTotal={dataItem?.resumoConsulta?.protestos?.valorTotal}
                    protestos={dataItem.ocorrencias}
                    temVencimento={false}
                    isNacional={true}
                  />
                ) : (
                  <NoOcurrence />
                )}
              </>
            ) : (
              <div>
                <TitleStripe className="mb-4">
                  Protesto Nacional (Indisponível)
                </TitleStripe>
                <div className="bg-gray-100 p-4 rounded-lg text-xs flex gap-2 items-center mt-4">
                  <FiAlertCircle />
                  <div>
                    O protesto nacional está{" "}
                    <span className="font-semibold">
                      temporariamente indisponível
                    </span>{" "}
                    no momento. Você pode consultar as informações diretamente
                    no site oficial:{" "}
                    <a
                      className="underline"
                      target="_blank"
                      href="https://www.pesquisaprotesto.com.br"
                    >
                      Clicando aqui
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case "chequesSemFundo":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <>
                <TitleStripe>Cheques sem fundo</TitleStripe>
                {dataItem?.ocorrencias?.length > 0 ? (
                  <ChequesSemFundos chequesSemFundos={dataItem.ocorrencias} />
                ) : (
                  <NoOcurrence />
                )}
              </>
            )}
          </div>
        );
      case "scrBacen":
        return (
          <div>
            <ScrBacenQuery data={dataItem.scrBacen} />
          </div>
        );
      case "scorePF":
        return <ScorePFQuery data={dataItem} />;
      case "scorePJ":
        return <ScorePJQuery data={dataItem} />;
      case "cadin":
        return <CadinQuery data={dataItem} />;
      case "acoesProcessos":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <JudicialProcesses data={dataItem} />
            )}
          </div>
        );
      case "dividas":
        return <DividasQuery data={dataItem} dataFull={data} />;
      case "scpcNet":
        return <SCPCQuery data={dataItem} />;
      case "creditoPreditivo":
        return <MotorCredito data={dataItem} />;
      case "scoreRating":
        return <ScoreRating score={dataItem} data={data} />;
      case "rendaPresumidaPF":
        return <RendaPresumidaPF data={dataItem} />;
      case "gastoEstimadoPF":
        return <GastoEstimadoPF data={dataItem} />;
      default:
        return null;
    }
  };

  const renderSortedData = () => {
    if (!data) return null;
    const itemsArray = Object.entries(data)
      .filter(([_, value]) => value && value.prioridade != null)
      .sort(([, a], [, b]) => a.prioridade - b.prioridade);

    const anyActiveFinance = Array.from(FINANCE_KEYS).some(
      (k) => data?.[k]?.statusRetorno == 1
    );
    let financeRendered = false;

    return itemsArray.map(([key, dataItem]) => {
      if (FINANCE_KEYS.has(key)) {
        if (financeRendered) return null;
        financeRendered = true;
        return anyActiveFinance ? (
          <InformacoesFinanceirasPJQuery key="__finance__" dataFull={data} />
        ) : null;
      }
      return (
        <React.Fragment key={key}>
          {renderComponentByKey(key, dataItem, document)}
        </React.Fragment>
      );
    });
  };

  const handleAnaliseIA = async () => {
    setIsLoadingIA(true);
    try {
      const { data: response } = await axios.post(
        `${QUERIES.ANALISAR_CREDITO}`,
        data
      );
      setResultadoIA(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingIA(false);
    }
  };

  /* ==========================
     Resultado reutilizável (desktop e modal)
     ========================== */
  const ResultContent = () => (
    <>
      <PreparePrint isPrinting={isPrinting} />
      {!isLoading && data && (
        <Card className="card-pdf p-5 relative overflow-hidden pdf-sheet">
          {query?.url !== "serasa-premium" && (
            <HeaderQuery
              title={query?.name}
              logoURL={
                query?.name?.toLowerCase().includes("imobiliario") &&
                "/assets/logo_casa.png"
              }
              date={data?.resumoRetorno?.dataConsulta}
              protocol={data?.resumoRetorno?.protocolo}
            />
          )}

          <div className="flex items-start gap-3 rounded-xl border border-yellow-300 bg-yellow-50/90 px-5 py-4">
            <div className="flex-1">
              <p className="font-semibold text-yellow-900">Informativo</p>
              <p className="mt-0.5 text-sm text-yellow-800 leading-snug">
                Ressaltamos que a análise de crédito e seu resultado são de
                responsabilidade exclusiva do cliente associado.
              </p>
            </div>
          </div>

          {isLoadingIA && <LoadingAnaliseIA />}

          {customQuery?.QueryCustomized?.hasIA &&
            !resultadoIA &&
            !isLoadingIA &&
            query?.url !== "serasa-premium" && (
              <div className="print:hidden">
                <AnaliseDeCreditoPlaceholder onClick={handleAnaliseIA} />
              </div>
            )}

          {resultadoIA && <CreditoPreditivo data={resultadoIA} />}

          {customQuery?.QueryCustomized?.showAlertsBoard && (
            <CustomAlertWrapper data={data} />
          )}

          {customQuery?.QueryCustomized?.hasTabs ? (
            <TabbedBodyReview
              data={data}
              document={document}
              renderComponentByKey={renderComponentByKey}
            />
          ) : (
            <>
              {renderSortedData()}
              {data?.additional && (
                <CustomCreditQueryAdditional data={data?.additional} />
              )}
            </>
          )}

          <FooterLGPD />
        </Card>
      )}
    </>
  );

  return (
    <>
      <PreparePrint isPrinting={isPrinting} />

      <div className="relative mb-4">
        <CardQueryDescription
          title={query?.name || `Informações da consulta`}
          description={query?.description}
          price={query?.price}
          additionalOptions={permittedAdditionalOptions}
          selectedAdditionalIds={selectedAdditionalIds}
          onChangeAdditionalIds={setSelectedAdditionalIds}
          tags={query?.tags}
        >
          <form
            className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-sm border border-gray-200 w-full"
            style={{ maxWidth: "500px" }}
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col md:flex-row gap-2 items-center z-10">
              <QueryInput
                info={info}
                setInfo={setInfo}
                isLoading={isLoading}
                error={error}
                type={customQuery?.QueryCustomized?.parameterType}
              />
            </div>
          </form>

          <div className="flex gap-2">
            {!errorQuery && data && (
              <Button className="bg-gray-600 hidden md:block" onClick={printFn}>
                <div className="flex gap-2 items-center">
                  <FaPrint className="hidden md:block" />
                  <div className="text-2xs md:text-sm">Imprimir</div>
                </div>
              </Button>
            )}
            {!errorQuery && data && (
              <Button className="primary hidden md:block" onClick={savePDFFn}>
                <div className="flex gap-2 items-center">
                  <FaRegFilePdf className="hidden md:block" />
                  <div className="text-2xs md:text-sm whitespace-nowrap">
                    Salvar PDF
                  </div>
                </div>
              </Button>
            )}
          </div>
        </CardQueryDescription>
        {isLoadingCustomQuery && <DrawerLoading />}
      </div>

      {data && (
        <div className="flex flex-col md:flex-row items-center bg-white p-2 gap-2 rounded-md shadow-md mb-4 hidden md:flex">
          <div className="font-semibold text-sm">
            Deseja realizar uma nova consulta?
          </div>
          <Button
            onClick={() => {
              setData(null);
              setInfo("");
              setResultadoIA(null);
              setShowMobileModal(false);
            }}
          >
            Realizar nova consulta
          </Button>
        </div>
      )}

      {errorQuery ? (
        <ErrorQuery status={errorQuery} />
      ) : (
        <>
          {/* DESKTOP: mantém como antes */}
          <div
            ref={componentRef}
            className={`
    overflow-x-scroll print-content mobile-fit mx-auto scroll-mt-24
    ${data || isLoading ? "md:block hidden" : "hidden"}
  `}
          >
            {!isLoading && data && <ResultContent />}
          </div>

          {/* MOBILE: resultado em MODAL */}
          {isMobile && (data || !isLoading) && (
            <Modal
              show={showMobileModal}
              onClose={() => setShowMobileModal(false)}
              size="7xl"
            >
              <ModalHeader className="py-2">
                {!errorQuery && data && (
                  <div className="ml-auto flex gap-2">
                    <Button size="xs" onClick={savePDFFn}>
                      <FaRegFilePdf className="mr-1" /> Salvar PDF
                    </Button>
                  </div>
                )}
              </ModalHeader>
              <ModalBody className="py-2 px-2">
                <div
                  className="overflow-x-hidden"
                  style={{ maxHeight: "480px" }}
                  ref={(el) => {
                    componentRef.current = el;
                    printRef.current = el;
                  }}
                >
                  <ResultContent />
                </div>
              </ModalBody>
            </Modal>
          )}
        </>
      )}

      {isLoading && (
        <ConsultaLoading
          fullscreen
          brandName="Relinq"
          accent="brand"
          tips={[
            "Autenticando provedores",
            "Buscando dados das fontes confiáveis",
            "Verificando ocorrências",
            "Consolidando informações",
            "Sincronizando resultados com a base interna",
            "Preparando indicadores",
            "Revisando alertas e pendências",
            "Carregando módulos extras de análise",
          ]}
        />
      )}
    </>
  );
}
